<div class="container-fluid complyon-page complaints-page px-3 px-md-4 px-xl-4 py-3">
  <div class="complaints-shell mx-auto">
    <section class="mb-3">
      <h1 class="complyon-title complaints-title mb-2 text-l">Complaints & grievances</h1>
      <p class="complyon-subtitle complaints-subtitle mb-0 text-s">
        Complaints from employees, suppliers, citizens, beneficiaries and customers — logged, categorised, routed to a department against an SLA, investigated and closed with outcome evidence.
      </p>
    </section>

    <section class="row g-3 mb-3">
      <div v-for="card in cards" :key="card.label" class="col-12 col-md-6 col-xl-3">
        <article class="complyon-card complaints-stat-card rounded-4 p-3 h-100">
          <div class="complyon-stat-label text-xs">{{ card.label }}</div>
          <div class="complyon-stat-value text-l" :class="card.accent === 'red' ? 'text-danger' : card.accent === 'green' ? 'text-success' : 'text-dark'">
            {{ card.value }}
          </div>
          <div class="complyon-stat-note mt-3 text-xs">{{ card.note }}</div>
        </article>
      </div>
    </section>

    <section class="complyon-panel complaints-panel rounded-4 overflow-hidden">
      <div class="d-flex align-items-start justify-content-between gap-3 px-3 pt-3 pb-2">
        <div class="min-w-0">
          <div class="complyon-section-title text-s">Complaints & grievances</div>
          <div class="complyon-section-subtitle text-xs">
            Complaints from employees, suppliers, citizens, beneficiaries and customers — captured, categorised, routed to a responsible department against an SLA, investigated and closed with outcome evidence and satisfaction tracking.
          </div>
        </div>
        <button type="button" class="btn config-navbar-primary-action complaints-log-btn flex-shrink-0">
          <i class="bi bi-plus-lg"></i>
          <span>Log complaint</span>
        </button>
      </div>

      <div class="complaints-toolbar px-3 pb-2">
        <div class="d-flex flex-wrap align-items-center gap-2">
          <button
            v-for="filter in filters"
            :key="filter.id"
            type="button"
            class="btn complaints-filter-chip"
            :class="{ active: active_filter === filter.id }"
            @click="set_filter(filter.id)"
          >
            <span>{{ filter.label }}</span>
            <span>{{ filter.count }}</span>
          </button>
        </div>

        <div class="complaints-search-wrap">
          <i class="bi bi-search"></i>
          <input
            v-model="search_query"
            type="text"
            class="form-control complaints-search"
            placeholder="Search..."
          />
        </div>
      </div>

      <div class="table-responsive">
        <table class="table complyon-table complaints-table align-middle mb-0">
          <thead>
            <tr>
              <th class="ps-3 text-xs">Ref</th>
              <th class="text-xs">Complaint</th>
              <th class="text-xs">Category</th>
              <th class="text-xs">Complainant</th>
              <th class="text-xs">Department</th>
              <th class="text-xs">SLA</th>
              <th class="text-xs">Status</th>
              <th class="pe-3"></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in filtered_rows"
              :key="row.ref"
              class="complaints-row"
              :class="{ 'is-active': active_complaint_ref === row.ref }"
              tabindex="0"
              @click="open_complaint_drawer(row)"
              @keydown.enter.prevent="open_complaint_drawer(row)"
              @keydown.space.prevent="open_complaint_drawer(row)"
            >
              <td class="ps-3 fw-semibold text-success">{{ row.ref }}</td>
              <td class="fw-semibold complaints-matter">{{ row.complaint }}</td>
              <td>{{ row.category }}</td>
              <td>{{ row.complainant }}</td>
              <td>{{ row.department }}</td>
              <td class="fw-semibold">{{ row.sla }}</td>
              <td><span class="badge complyon-badge" :class="row.status.class">{{ row.status.label }}</span></td>
              <td class="pe-3 text-end"><i class="bi bi-chevron-right finance-arrow"></i></td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  </div>

  <teleport to="body">
    <div
      v-if="active_complaint && active_complaint_drawer"
      class="command-centre-drawer-backdrop"
      @click.self="close_complaint_drawer"
    ></div>

    <aside
      v-if="active_complaint && active_complaint_drawer"
      class="command-centre-drawer complaints-drawer"
      role="dialog"
      aria-modal="true"
      aria-label="Complaint drawer"
    >
      <header class="command-centre-drawer-header complaints-drawer-header border-bottom">
        <div class="d-flex align-items-start justify-content-between gap-3">
          <div class="min-w-0">
            <div class="complaints-drawer-kicker">
              {{ String(active_complaint_drawer.kicker || 'Complaint').toUpperCase() }} · {{ active_complaint.ref }}
            </div>
            <div class="complaints-drawer-title mt-1">{{ active_complaint.complaint }}</div>
            <div class="complaints-drawer-subtitle mt-1">{{ active_complaint_drawer.subtitle }}</div>
          </div>
          <div class="d-flex align-items-start gap-2 flex-shrink-0">
            <span class="badge rounded-pill complaints-status-pill" :class="active_complaint_drawer.badge.class">
              {{ active_complaint_drawer.badge.label }}
            </span>
            <button type="button" class="btn btn-link p-0 command-centre-drawer-close" @click="close_complaint_drawer" aria-label="Close drawer">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>
        </div>
      </header>

      <div class="command-centre-drawer-body complaints-drawer-body">
        <section class="complaints-drawer-details">
          <div v-for="item in active_complaint_drawer.details" :key="`${active_complaint.ref}-${item.label}`" class="complaints-drawer-detail-row">
            <div class="complaints-drawer-detail-label">{{ item.label }}</div>
            <div class="complaints-drawer-detail-value">{{ item.value }}</div>
          </div>
        </section>

        <section v-if="active_complaint_drawer.checks" class="complaints-drawer-section">
          <div class="d-flex align-items-center justify-content-between gap-2">
            <div class="complaints-drawer-section-title">{{ active_complaint_drawer.checks.title }}</div>
            <span class="badge rounded-pill complaints-section-pill bg-warning-subtle text-warning-emphasis">
              {{ active_complaint_drawer.checks.badge }}
            </span>
          </div>
          <div class="d-grid gap-2 mt-2">
            <article v-for="item in active_complaint_drawer.checks.items" :key="`${active_complaint.ref}-${item.title}`" class="complaints-check-card">
              <div class="d-flex align-items-start justify-content-between gap-2">
                <div class="d-flex align-items-start gap-2 min-w-0">
                  <span class="complaints-check-icon" :class="complaint_check_state_class(item.state)">
                    <i class="bi" :class="complaint_check_icon(item.state)"></i>
                  </span>
                  <div class="min-w-0">
                    <div class="complaints-check-title">{{ item.title }}</div>
                    <div class="complaints-check-subtitle">{{ item.subtitle }}</div>
                  </div>
                </div>
                <span class="badge rounded-pill complaints-check-pill" :class="complaint_check_badge_class(item.state)">
                  {{ item.state_label }}
                </span>
              </div>
            </article>
          </div>
        </section>

        <section class="complaints-drawer-section">
          <div class="complaints-drawer-section-title">Connected records</div>
          <div class="complaints-connected-grid mt-2">
            <article v-for="record in active_complaint_drawer.records" :key="`${active_complaint.ref}-${record.type}-${record.title}`" class="complaints-connected-card">
              <div class="d-flex align-items-start gap-2">
                <div class="complaints-connected-icon">
                  <i class="bi" :class="complaint_record_icon(record.type)"></i>
                </div>
                <div class="min-w-0 flex-grow-1">
                  <div class="complaints-connected-type">{{ record.type }}</div>
                  <div class="complaints-connected-title">{{ record.title }}</div>
                </div>
                <i class="bi bi-box-arrow-up-right complaints-connected-link"></i>
              </div>
            </article>
          </div>
        </section>

        <section class="complaints-drawer-section">
          <div class="d-flex align-items-center justify-content-between gap-2">
            <div class="complaints-drawer-section-title">Recommended action</div>
            <span class="badge rounded-pill complaints-ai-mini-pill">AI</span>
          </div>
          <div class="complaints-recommendation-card mt-2">
            {{ active_complaint_drawer.recommendation }}
          </div>
        </section>
      </div>

      <footer class="command-centre-drawer-footer complaints-drawer-footer border-top">
        <div class="d-flex align-items-center justify-content-between gap-2 flex-wrap">
          <button type="button" class="btn btn-link text-decoration-none complaints-drawer-link">
            <i class="bi bi-flag me-1"></i>
            {{ active_complaint_drawer.footer.left_label }}
          </button>
          <div class="d-flex align-items-center gap-2">
            <button type="button" class="btn btn-outline-secondary complaints-drawer-secondary">
              <i class="bi bi-folder2-open me-1"></i>
              {{ active_complaint_drawer.footer.secondary_label }}
            </button>
            <button type="button" class="btn config-navbar-primary-action complaints-drawer-primary">
              <i class="bi bi-arrow-right-short me-1"></i>
              {{ active_complaint_drawer.footer.primary_label }}
            </button>
          </div>
        </div>
      </footer>
    </aside>
  </teleport>
</div>
